<script setup lang="ts">
import { onMounted, ref, unref } from 'vue';
import { useTenantSettingStore } from '@/store';
import { storeToRefs } from 'pinia';
import { TenantSettingsInterface } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';

const tenantSettingsStore = useTenantSettingStore();
const { loading } = storeToRefs(tenantSettingsStore);

const settings = ref<TenantSettingsInterface[]>([]);
const enable = ref<boolean>(false);

const setSettings = async () => {
  settings.value = structuredClone(tenantSettingsStore.settings);
  settings.value.forEach((setting) => {
    setting.checked = setting.value === 1 ? true : false;
  });
};
const init = async () => {
  if (can([PERMISSIONS.TENANT_SETTINGS_UPDATE])) {
    await tenantSettingsStore.getTenantSettings();
    await setSettings();
  } else window.location.href = '/';
};

onMounted(() => {
  init();
});

const cancel = () => {
  enable.value = false;
  setSettings();
};

const save = async () => {
  const payload = unref(settings);

  payload.forEach(async (setting) => {
    await tenantSettingsStore.updateSettings(setting);
  });

  init();
};

const changeValue = (data: TenantSettingsInterface) => {
  enable.value = true;
  data.value = data.value === 1 ? 0 : 1;
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Feature Flags</h2>
    </template>
    <template #content>
      <div v-if="loading.settings">...loading</div>
      <div v-else>
        <DataTable :scrollable="true" :value="settings" responsive-layout="scroll" scroll-height="flex">
          <template v-if="settings.length">
            <Column header="Name" field="name" header-class="required">
              <template #body="{ data }">
                <span class="w-10 font-semibold" v-text="data.name" />
              </template>
            </Column>
            <Column class="px-2 gap-2" header="Enable/Disable">
              <template #body="{ data }">
                <div class="text-center justify-content-center">
                  <Checkbox v-model="data.checked" :binary="true" @change="changeValue(data)" />
                </div>
              </template>
            </Column>
          </template>
        </DataTable>
      </div>
    </template>
    <template #footer>
      <AppButton text="Cancel" type="secondary" @click="cancel" />
      <AppButton :disabled="!enable" text="Save" type="primary" @click="save" />
    </template>
  </Card>
</template>

<style scoped lang="scss">
.error {
  color: red;
}

:deep(th.required) {
  span.p-column-title::after {
    content: '*';
    color: #ff3960;
    margin-left: 0.125rem;
  }
}
</style>
